import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { ChevronRight, PackagePlus } from 'lucide-react'
import { toast } from 'sonner'
import {
  usePushListing,
  useSaveListingPrice,
  useItem,
  useWarehouses,
  type QuickShipListing,
  type StockCondition,
  type UsedGrade,
  type WarehouseItemDetail,
} from '@/api/warehouse'
import { formatApiErrorMessage } from '@/lib/api-error'
import { BackButton } from '@/components/back-button'
import { POLineItemProductCell } from '@/components/po-line-item-product-cell'
import { StatusPill } from '@/components/status-pill'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { usePageTitle } from '@/lib/page-title'
import { cn, formatDateOnly, formatMoney } from '@/lib/utils'
import { QuickShipListingBlock } from './listing-block'
import { casesNote, locationLabel, quantityLabel, stockStatusPill } from './model'
import { ReceiveStockSheet } from './receive-stock-sheet'
import { ConditionPill } from './stock-table'

function Totals({ item }: { item: WarehouseItemDetail }) {
  const perCase = item.product.units_per_case
  const cells: Array<[string, number]> = [
    ['On hand', item.quantity_on_hand],
    ['Reserved', item.quantity_reserved],
    ['Available', item.quantity_available],
  ]
  const note = casesNote(item.quantity_on_hand, perCase)
  return (
    <div className="border-t pt-3">
      <dl className="grid grid-cols-3 gap-2">
        {cells.map(([label, value]) => (
          <div key={label}>
            <dt className="text-xs text-muted-foreground">{label}</dt>
            <dd className="text-xl font-semibold tabular-nums">{quantityLabel(value, perCase)}</dd>
          </div>
        ))}
      </dl>
      {note && <p className="mt-1 text-xs text-muted-foreground">{note}</p>}
    </div>
  )
}

/** Every place this item sits, largest first; each opens its own page with its history. */
function Locations({ item, showWarehouse }: { item: WarehouseItemDetail; showWarehouse: boolean }) {
  const perCase = item.product.units_per_case
  return (
    <section aria-labelledby="item-locations" className="overflow-hidden rounded-xl border bg-card">
      <h2 id="item-locations" className="border-b px-4 py-3 text-sm font-semibold">
        Locations
      </h2>
      <ul className="divide-y">
        {item.lots.map((lot) => {
          const status = stockStatusPill(lot)
          return (
            <li key={lot.uuid}>
              <Link
                to="/warehouse/$stockId"
                params={{ stockId: lot.uuid }}
                className={cn(
                  'flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-muted/40',
                  status && 'text-muted-foreground',
                )}
              >
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2 text-sm font-medium">
                    {locationLabel(lot) ?? <span className="font-normal text-muted-foreground">No bay</span>}
                    {status && <StatusPill tone={status.tone}>{status.label}</StatusPill>}
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {[
                      showWarehouse ? lot.warehouse.name : null,
                      `Received ${formatDateOnly(lot.received_at)}`,
                      lot.unit_net != null ? `${formatMoney(lot.unit_net)} Net each` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
                <span className="shrink-0 text-right tabular-nums">
                  <span className="text-sm font-semibold">{quantityLabel(lot.quantity_on_hand, perCase)}</span>
                  {lot.quantity_reserved > 0 && (
                    <span className="block text-[11px] text-muted-foreground">{lot.quantity_reserved} reserved</span>
                  )}
                </span>
                <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              </Link>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

/** The listing with its price click-to-edit and a retry after a failed push. */
function EditableListing({ listing }: { listing: QuickShipListing }) {
  const savePrice = useSaveListingPrice(listing.uuid)
  const push = usePushListing(listing.uuid)
  return (
    <QuickShipListingBlock
      listing={listing}
      pricePending={savePrice.isPending}
      onSavePrice={(price) =>
        savePrice.mutate(price, { onError: (error) => toast.error(formatApiErrorMessage('Price not saved', error)) })
      }
      pushPending={push.isPending}
      onPushNow={() =>
        push.mutate(undefined, { onError: (error) => toast.error(formatApiErrorMessage('Not sent to the store', error)) })
      }
    />
  )
}

export function ItemPage({
  productUuid,
  condition,
  grade,
}: {
  productUuid: string
  condition: StockCondition
  grade: UsedGrade | null
}) {
  const { data: item, isLoading, isError } = useItem(productUuid, condition, grade)
  const warehouses = useWarehouses().data ?? []
  const [receiving, setReceiving] = useState(false)
  usePageTitle(item ? `${item.product.model} · Warehouse` : null)

  if (isLoading) {
    return (
      <div className="mx-auto max-w-[1600px] space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }
  if (isError || !item) {
    return <p className="py-12 text-center text-muted-foreground">Not in the warehouse.</p>
  }
  const { product } = item

  return (
    <div className="mx-auto min-w-0 max-w-[1600px]">
      <nav className="flex min-w-0 items-center gap-1 text-sm text-muted-foreground">
        <BackButton iconOnly fallback={{ to: '/warehouse' }} fallbackLabel="Back to Warehouse" />
        <Link to="/warehouse" className="hover:text-foreground hover:underline">
          Warehouse
        </Link>
      </nav>

      <div className="mb-5 mt-1 flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <h1 className="break-words text-lg font-bold tracking-tight sm:text-xl md:text-2xl">{product.model}</h1>
          <ConditionPill stock={item} />
        </div>
        <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => setReceiving(true)}>
          <PackagePlus className="size-3.5" aria-hidden />
          Receive more
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="min-w-0 space-y-6">
          <Card className="gap-0 py-0">
            <CardContent className="space-y-3 p-4">
              <POLineItemProductCell
                model={product.model}
                description={product.store_title}
                productUuid={product.uuid}
                imageUrl={product.image_url}
                manufacturerName={product.manufacturer.short_name || product.manufacturer.name}
                manufacturerUuid={product.manufacturer.uuid}
                imageClassName="size-16"
              />
              <Totals item={item} />
            </CardContent>
          </Card>
          <Locations item={item} showWarehouse={warehouses.length > 1} />
        </div>
        <div className="min-w-0">
          {item.listing ? <EditableListing listing={item.listing} /> : <QuickShipListingBlock listing={null} />}
        </div>
      </div>

      <ReceiveStockSheet
        open={receiving}
        onOpenChange={setReceiving}
        preset={{
          product: {
            uuid: product.uuid,
            model: product.model,
            store_title: product.store_title,
            manufacturer_name: product.manufacturer.short_name || product.manufacturer.name,
            image_url: product.image_url,
            units_per_case: product.units_per_case,
          },
          condition: item.condition,
          grade: item.used_grade ?? null,
        }}
      />
    </div>
  )
}
